import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { fetchFollowUpBatchOptions } from "../../../utils/api";

const MIN_QUERY_LENGTH = 2;
const DEFAULT_LIMIT = 20;

const patientSearchFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "4px",
    fontSize: "0.875rem",
  },
  "& .MuiOutlinedInput-input": {
    py: 0,
  },
};

const BarcodePicker = ({
  value = "",
  onChange,
  label = "Barcode",
  placeholder = "Barcode starts with",
  sx,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    if (!open) return;

    const trimmed = inputValue.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setOptions(value ? [value] : []);
      setTruncated(false);
      setLoading(false);
      return;
    }

    let active = true;
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetchFollowUpBatchOptions({
          q: trimmed,
          limit: DEFAULT_LIMIT,
        });
        let fetched = Array.isArray(response?.items) ? response.items : [];
        if (!inputValue.trim() && value && !fetched.includes(value)) {
          fetched = [value, ...fetched];
        }
        if (active) {
          setOptions(fetched);
          setTruncated(Boolean(response?.truncated));
        }
      } catch (error) {
        if (active) {
          setOptions(value ? [value] : []);
          setTruncated(false);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [inputValue, open, value]);

  useEffect(() => {
    if (open) {
      setInputValue("");
    }
  }, [open]);

  const handleSelect = (selectedValue) => {
    onChange?.(selectedValue);
    setAnchorEl(null);
  };

  const selectedLabel = value ? value : "All Barcodes";

  return (
    <>
      <Button
        variant={value ? "contained" : "outlined"}
        size="small"
        startIcon={<MenuIcon fontSize="small" />}
        endIcon={<KeyboardArrowDownIcon fontSize="small" />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="Select barcode"
        sx={{
          minWidth: 180,
          height: 40,
          position: "relative",
          justifyContent: "center",
          px: 4,
          "& .MuiButton-startIcon": {
            position: "absolute",
            left: 12,
            margin: 0,
          },
          "& .MuiButton-endIcon": {
            position: "absolute",
            right: 12,
            margin: 0,
          },
          ...sx,
        }}
      >
        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selectedLabel}
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            width: anchorEl ? anchorEl.clientWidth : 240,
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 0,
            border: "1px solid #000",
            display: "flex",
            flexDirection: "column",
            maxHeight: 460,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 1.5, borderBottom: "1px solid #d0d0d0", bgcolor: "#fff" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              placeholder={placeholder}
              size="small"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              sx={{ ...patientSearchFieldSx, flex: 1 }}
              fullWidth
            />
            {loading && <CircularProgress size={18} />}
          </Stack>
          {inputValue.trim().length > 0 && inputValue.trim().length < MIN_QUERY_LENGTH && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Type at least {MIN_QUERY_LENGTH} characters to search.
            </Typography>
          )}
        </Box>

        <List disablePadding sx={{ flex: 1, overflowY: "auto", bgcolor: "#fff" }}>
          <ListItemButton
            selected={!value}
            onClick={() => handleSelect("")}
            sx={{
              borderBottom: "1px solid #eeeeee",
              "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
              "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
            }}
          >
            <ListItemText
              primary="All Barcodes"
              secondary="Show all batches"
              primaryTypographyProps={{ fontWeight: 700, variant: "body2" }}
              secondaryTypographyProps={{ variant: "body2" }}
            />
          </ListItemButton>

          {options.map((option) => (
            <ListItemButton
              key={option}
              selected={value === option}
              onClick={() => handleSelect(option)}
              sx={{
                borderBottom: "1px solid #eeeeee",
                py: 1.25,
                "&.Mui-selected": { bgcolor: "rgba(80, 0, 0, 0.10)" },
                "&.Mui-selected:hover": { bgcolor: "rgba(80, 0, 0, 0.14)" },
              }}
            >
              <ListItemText
                primary={option}
                primaryTypographyProps={{
                  fontWeight: value === option ? 700 : 600,
                  variant: "body2",
                }}
                sx={{ my: 0 }}
              />
            </ListItemButton>
          ))}

          {!loading && options.length === 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {inputValue.trim().length < MIN_QUERY_LENGTH
                  ? "Type at least 2 characters to search."
                  : "No barcodes found."}
              </Typography>
            </Box>
          )}
        </List>
        {truncated && (
          <Box sx={{ px: 1.5, py: 1, borderTop: "1px solid #eeeeee", bgcolor: "#fff" }}>
            <Typography variant="caption" color="text.secondary">
              Showing the first {DEFAULT_LIMIT} matches. Keep typing to narrow the list.
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  );
};

export default BarcodePicker;
